<template>
  <!-- One merge card on Kliendid -> Otsi duplikaate (new look): the clients
       side by side with what matches marked, what each one has, which one
       stays and what happens. The same card for a group the scan found, one
       marked "Pole duplikaat" and a manual pick (mode); a shared-contact group
       of many first asks which of them really are the same client. -->
  <section class="cd" :class="{ 'cd--check': checkFirst || group.big }">
    <div class="cd-head">
      <h2 class="cd-title">{{ title }}</h2>
      <span v-for="tag in tags" :key="tag.text" class="cd-pill" :class="'cd-pill--' + tag.tone"><i />{{ tag.text }}</span>
      <span class="cd-grow" />
      <span v-if="mode === 'hidden' && group.hiddenBy" class="cd-sub">peitis {{ group.hiddenBy }}<template v-if="group.hiddenAt"> {{ day(group.hiddenAt) }}</template></span>
      <span class="cd-count">{{ group.clients.length }} KLIENTI</span>
    </div>

    <div v-if="done" class="cd-done">
      <v-icon size="18">mdi-check</v-icon>{{ done }}
      <span class="cd-grow" />
      <router-link v-if="doneId" :to="{ name: 'client/view/info', params: { clientId: doneId } }" class="cd-link">Ava klient</router-link>
    </div>

    <template v-else>
      <template v-if="group.big">
        <div class="cd-bignote">Nii palju erinevaid kliente ühe kontaktiga on enamasti haldur või kontor, mitte üks klient. Märgi ainult need, kes on tegelikult sama klient.</div>
        <div class="cd-biglist">
          <label v-for="client in group.clients" :key="client.id" class="cd-bigrow">
            <input v-model="picked" type="checkbox" :value="client.id" :aria-label="'Vali ' + client.fullName">
            <span><b>{{ client.fullName || '—' }}</b><span class="cd-sub"> · id {{ client.id }}</span></span>
            <span><mark v-if="matches('phone', client)">{{ client.phone }}</mark><template v-else>{{ client.phone || '—' }}</template></span>
            <span class="cd-sub">{{ placeOf(client) || 'Objekti pole' }}</span>
            <span class="cd-sub">{{ client.objects }} obj · {{ client.works }} töökäsku</span>
          </label>
        </div>
      </template>

      <div v-if="listed.length >= 2 || (!group.big && listed.length)" class="cd-grid" :style="{ gridTemplateColumns: 'repeat(' + Math.min(listed.length, 4) + ', minmax(0, 1fr))' }">
        <div v-for="client in listed" :key="client.id" class="cd-cand" :class="{ 'cd-cand--keep': shown.length > 1 && keepId === client.id, 'cd-cand--out': isOut(client) }">
          <div class="cd-top">
            <span class="cd-kind" :class="{ 'cd-kind--co': client.type === COMPANY }" :title="client.type === COMPANY ? 'Firma' : 'Eraklient'">
              <v-icon size="16">{{ client.type === COMPANY ? 'mdi-domain' : 'mdi-account' }}</v-icon>
            </span>
            <div class="cd-name">
              <router-link :to="{ name: 'client/view/info', params: { clientId: client.id } }" class="cd-link cd-link--name">
                <mark v-if="matches('name', client)">{{ client.fullName }}</mark><template v-else>{{ client.fullName || '—' }}</template>
              </router-link>
              <span v-if="client.problematic" class="cd-pill cd-pill--red">Probleemne</span>
              <div class="cd-sub">lisatud {{ day(client.added) }} · id {{ client.id }}</div>
            </div>
            <button v-if="mode === 'manual'" type="button" class="cd-x" aria-label="Eemalda valikust" @click="$emit('remove', client.id)">×</button>
          </div>
          <dl class="cd-kv">
            <dt>Telefon</dt>
            <dd><mark v-if="matches('phone', client)">{{ client.phone }}</mark><template v-else-if="client.phone">{{ client.phone }}</template><span v-else class="cd-miss">puudub</span></dd>
            <dt>E-post</dt>
            <dd><mark v-if="matches('email', client)">{{ client.email }}</mark><template v-else-if="client.email">{{ client.email }}</template><span v-else class="cd-miss">puudub</span></dd>
          </dl>
          <div class="cd-nums">
            <span :class="{ 'cd-zero': !client.objects }"><b>{{ client.objects }}</b>objekti<template v-if="client.contracts"> ({{ client.contracts }} lep.)</template></span>
            <span :class="{ 'cd-zero': !client.works }"><b>{{ client.works }}</b>töökäsku</span>
            <span :class="{ 'cd-zero': !client.akts }"><b>{{ client.akts }}</b>akti</span>
            <span v-if="client.unpaid" class="cd-pill cd-pill--amber">{{ client.unpaid }} maksmata</span>
          </div>
          <div class="cd-sub">{{ placeOf(client) || 'Objekti pole' }}</div>
          <div class="cd-sub">
            <template v-if="client.lastWork">
              <span v-if="client.lastTechnic" class="cd-ini" :style="initialsStyle(client.lastTechnic.color)" :title="client.lastTechnic.fullName">{{ initialsOf(client.lastTechnic.fullName) }}</span>viimane töökäsk {{ day(client.lastWork) }}
            </template>
            <template v-else>Töökäske pole olnud</template>
          </div>
          <div v-if="isOut(client)" class="cd-out">Jääb kõrvale — seda ei ühendata</div>
          <button
            v-else-if="shown.length > 1"
            type="button"
            role="radio"
            class="cd-keep"
            :aria-checked="keepId === client.id ? 'true' : 'false'"
            @click="chosen = client.id"
          ><span class="cd-dot" />{{ keepId === client.id ? (client.id === suggestedId ? 'Jääb alles (soovitus)' : 'Jääb alles') : 'Kustutatakse — vali see alles jääma' }}</button>
          <!-- Three or more: any of them can be left as it is. -->
          <button v-if="canLeaveOut" type="button" class="cd-leave" @click="toggleOut(client.id)">
            {{ isOut(client) ? 'Võta ühendamisse' : 'Jäta kõrvale' }}
          </button>
        </div>
      </div>

      <div class="cd-foot">
        <span class="cd-sum">{{ summary }}</span>
        <button v-if="mode === 'found'" type="button" class="cd-btn cd-btn--muted" :disabled="busy" @click="$emit('dismiss', group.clients.map(c => c.id))">Pole duplikaat</button>
        <button v-if="mode === 'hidden'" type="button" class="cd-btn cd-btn--muted" :disabled="busy" @click="$emit('restore', group.clients.map(c => c.id))">Taasta</button>
        <button type="button" class="cd-btn cd-btn--amber" :disabled="busy || shown.length < 2" @click="merge">
          {{ busy ? 'Ühendan…' : shown.length > 2 ? 'Ühenda ' + shown.length + ' → 1' : 'Ühenda' }}
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import dayjs from 'dayjs'
import { initials } from '@/components/frame/frameLinks'

const COMPANY = 2

// The client most worth keeping: contract objects, then the most history,
// then the one with both a phone and an e-mail, then the oldest.
const weight = c => (c.contracts || 0) * 1000 + (c.objects || 0) * 10 + (c.works || 0) + (c.phone ? 0.5 : 0) + (c.email ? 0.5 : 0)

export default {
  props: {
    group: { type: Object, required: true },
    // found | hidden | manual
    mode: { type: String, default: 'found' },
    busy: { type: Boolean, default: false },
    done: { type: String, default: '' },
    doneId: { type: Number, default: null }
  },
  emits: ['merge', 'dismiss', 'restore', 'remove'],
  data () {
    return { COMPANY, chosen: null, picked: [], leftOut: [] }
  },
  computed: {
    // On a shared-contact group, only the ones ticked as the same client.
    // The candidates on the card: on a shared-contact group only the ticked.
    listed () {
      return this.group.big ? this.group.clients.filter(c => this.picked.includes(c.id)) : this.group.clients
    },
    // Of those, the ones that go into the merge.
    shown () {
      return this.listed.filter(c => !this.leftOut.includes(c.id))
    },
    // With three or more on the card, any can be left as it is.
    canLeaveOut () {
      return !this.group.big && this.group.clients.length > 2
    },
    suggestedId () {
      const best = this.shown.slice().sort((a, b) => weight(b) - weight(a) || a.id - b.id)[0]
      return best ? best.id : null
    },
    keepId () {
      return this.shown.some(c => c.id === this.chosen) ? this.chosen : this.suggestedId
    },
    checkFirst () {
      return this.group.reasons && this.group.reasons.length === 1 && this.group.reasons[0] === 'name'
    },
    title () {
      if (this.mode === 'manual') return 'Ühenda käsitsi'
      if (this.group.big) {
        const phone = this.group.reasons.includes('phone') && this.sharedValues('phone').values().next().value
        const shownPhone = phone && (this.group.clients.find(c => c.keys && c.keys.phone === phone) || {}).phone
        return shownPhone ? 'Telefon ' + shownPhone : 'Ühine kontakt'
      }
      return (this.group.clients[0] && this.group.clients[0].fullName) || '—'
    },
    tags () {
      const g = this.group
      const tags = []
      if (this.mode === 'manual') return tags
      if (g.big) {
        tags.push({ tone: 'amber', text: 'Ühine kontakt ' + g.size + ' klienti — tõenäoliselt haldur või kontor' })
        return tags
      }
      if (g.strong) tags.push({ tone: 'ok', text: 'Sama nimi ja telefon' })
      else if (this.checkFirst) tags.push({ tone: 'amber', text: 'Ainult nimi kattub — kontrolli' })
      const labels = { phone: 'Sama telefon', email: 'Sama e-post', name: 'Sama nimi' }
      for (const reason of g.reasons || []) {
        if (g.strong && (reason === 'name' || reason === 'phone')) continue
        if (this.checkFirst && reason === 'name') continue
        tags.push({ tone: 'blue', text: labels[reason] })
      }
      if (g.easy) tags.push({ tone: 'grey', text: 'Teine on tühi — ohutu ühendada' })
      return tags
    },
    summary () {
      if (this.shown.length < 2) {
        if (this.leftOut.length) return 'Ühendamiseks jäta sisse vähemalt kaks klienti.'
        return this.group.big ? 'Märgi vähemalt kaks, kes on sama klient.' : 'Lisa veel vähemalt üks klient.'
      }
      const keep = this.shown.find(c => c.id === this.keepId)
      const rest = this.shown.filter(c => c.id !== this.keepId)
      const sum = key => rest.reduce((n, c) => n + (c[key] || 0), 0)
      const moves = [[sum('objects'), 'objekt', 'objekti'], [sum('works'), 'töökäsk', 'töökäsku'], [sum('akts'), 'akt', 'akti']]
        .filter(([n]) => n).map(([n, one, many]) => n + ' ' + (n === 1 ? one : many))
      // What the merge copies onto the kept client when it has none
      // (POST /client/merge's backfill), named before it happens.
      const fills = [['email', 'e-posti'], ['phone', 'telefoni'], ['clientAddress', 'aadressi']]
        .filter(([field]) => !keep[field] && rest.some(c => c[field]))
        .map(([field, label]) => label + ' ' + rest.find(c => c[field])[field])
      return 'Jääb ' + keep.fullName + '; kustutatakse ' + rest.length + '. ' +
        (moves.length ? 'Liigub ' + moves.join(', ') + '.' : 'Teistel pole midagi, mida liigutada.') +
        (fills.length ? ' ' + keep.fullName + ' saab ' + fills.join(' ja ') + '.' : '') +
        (this.leftOut.length ? ' ' + this.leftOut.length + ' jääb kõrvale nagu on' + (this.mode === 'manual' ? '.' : ' ega tule enam temaga koos duplikaadina ette.') : '')
    }
  },
  methods: {
    initialsOf: initials,
    initialsStyle (color) {
      if (!color) return { background: '#9AAAA4', color: '#fff' }
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      return { background: color, color: (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#16322A' : '#fff' }
    },
    day (value) {
      return value ? dayjs(value).format('DD.MM.YYYY') : ''
    },
    // The client's first object, where they have one.
    placeOf (client) {
      return client.objects ? [client.address, client.city].filter(Boolean).join(', ') : ''
    },
    // The values (as the server compares them) that two or more share.
    sharedValues (field) {
      const values = this.group.clients.map(c => c.keys && c.keys[field]).filter(v => v && String(v).trim())
      return new Set(values.filter((v, i) => values.indexOf(v) !== i))
    },
    matches (field, client) {
      return !!(client.keys && client.keys[field] && this.sharedValues(field).has(client.keys[field]))
    },
    isOut (client) {
      return this.leftOut.includes(client.id)
    },
    toggleOut (id) {
      this.leftOut = this.leftOut.includes(id) ? this.leftOut.filter(x => x !== id) : [...this.leftOut, id]
    },
    merge () {
      this.$emit('merge', {
        keepId: this.keepId,
        deleteIds: this.shown.filter(c => c.id !== this.keepId).map(c => c.id),
        leftOut: this.leftOut.filter(id => this.listed.some(c => c.id === id))
      })
    }
  }
}
</script>

<style scoped>
.cd {
  --d-ground: #F4F6F5; --d-surface: #FFFFFF; --d-ink: #16322A; --d-ink-2: #3E504A; --d-ink-3: #5B6B66;
  --d-line: #C9D2CE; --d-line-soft: #E1E7E4; --d-green: #2E7D32; --d-green-dark: #1B5E20; --d-green-bg: #EAF3EA;
  --d-blue: #1F5FD1; --d-blue-bg: #EEF3FC; --d-amber: #8F5400; --d-amber-bg: #FFF4E0; --d-amber-btn: #B26A00; --d-red: #B3261E; --d-red-bg: #FCEDEC;
  --d-mark: #FFE7A3;
  background: var(--d-surface); border: 1px solid var(--d-line-soft); border-radius: 10px; margin-bottom: 14px; overflow: hidden; color: var(--d-ink);
}
.cd button, .cd input { font: inherit; color: inherit; }
.cd--check { border-color: var(--d-amber); }
.cd-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--d-line-soft); }
.cd-title { font-size: 17px; font-weight: 600; margin: 0 6px 0 0; }
.cd-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; letter-spacing: .03em; padding: 2px 8px; border-radius: 999px; }
.cd-pill i { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.cd-pill--ok { background: var(--d-green-bg); color: var(--d-green-dark); }
.cd-pill--blue { background: var(--d-blue-bg); color: var(--d-blue); }
.cd-pill--amber { background: var(--d-amber-bg); color: var(--d-amber); }
.cd-pill--grey { background: var(--d-line-soft); color: var(--d-ink-3); }
.cd-pill--red { background: var(--d-red-bg); color: var(--d-red); margin-left: 4px; }
.cd-grow { flex: 1; }
.cd-count { color: var(--d-ink-3); font-size: 12px; letter-spacing: .06em; font-weight: 600; }
.cd-grid { display: grid; gap: 1px; background: var(--d-line-soft); }
.cd-cand { background: var(--d-surface); padding: 14px 16px; display: grid; gap: 8px; align-content: start; min-width: 0; }
.cd-cand--keep { background: #F2F8F2; }
.cd-top { display: flex; gap: 8px; align-items: start; }
.cd-kind { flex: none; width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; background: var(--d-line-soft); color: var(--d-ink-2); }
.cd-kind--co { background: var(--d-blue-bg); color: var(--d-blue); }
.cd-name { flex: 1; min-width: 0; font-weight: 600; font-size: 15px; overflow-wrap: anywhere; }
.cd-link { color: var(--d-blue); text-decoration: none; font-weight: 500; }
.cd-link:hover { text-decoration: underline; }
.cd-link--name { font-weight: 600; }
.cd-x { border: 0; background: none; font-size: 20px; line-height: 1; cursor: pointer; color: var(--d-ink-3); }
mark { background: var(--d-mark); color: inherit; border-radius: 3px; padding: 0 2px; }
.cd-kv { display: grid; grid-template-columns: 62px minmax(0, 1fr); gap: 2px 8px; font-size: 13px; margin: 0; }
.cd-kv dt { color: var(--d-ink-3); }
.cd-kv dd { margin: 0; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.cd-miss { color: var(--d-ink-3); font-style: italic; }
.cd-nums { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 13px; color: var(--d-ink-2); align-items: center; }
.cd-nums b { font-weight: 600; color: var(--d-ink); margin-right: 3px; font-variant-numeric: tabular-nums; }
.cd-zero b { color: var(--d-ink-3); }
.cd-sub { color: var(--d-ink-3); font-size: 12px; font-weight: 400; }
.cd-ini { display: inline-grid; place-items: center; width: 20px; height: 20px; border-radius: 50%; font-size: 9px; font-weight: 700; vertical-align: -5px; margin-right: 4px; }
.cd-keep { display: flex; align-items: center; gap: 8px; border: 1px solid var(--d-line); background: var(--d-surface); border-radius: 8px; padding: 7px 10px; cursor: pointer; text-align: left; font-size: 13px; color: var(--d-ink-2); }
.cd-dot { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--d-line); display: grid; place-items: center; flex: none; }
.cd-keep[aria-checked="true"] { border-color: var(--d-green); color: var(--d-green-dark); font-weight: 600; }
.cd-keep[aria-checked="true"] .cd-dot { border-color: var(--d-green); }
.cd-keep[aria-checked="true"] .cd-dot::after { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--d-green); }
.cd-cand--out { background: #F4F6F5; }
.cd-cand--out > :not(.cd-leave):not(.cd-out) { opacity: .5; }
.cd-out { font-size: 13px; color: var(--d-ink-3); border: 1px dashed var(--d-line); border-radius: 8px; padding: 7px 10px; }
.cd-leave { justify-self: start; border: 0; background: none; color: var(--d-blue); cursor: pointer; padding: 0; font-size: 13px; font-weight: 500; }
.cd-foot { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 12px 16px; border-top: 1px solid var(--d-line-soft); background: #F8FAF9; }
.cd-sum { flex: 1 1 320px; min-width: 0; font-size: 13px; color: var(--d-ink-2); }
.cd-btn { border: 1px solid var(--d-line); background: var(--d-surface); border-radius: 6px; padding: 7px 14px; font-weight: 600; cursor: pointer; }
.cd-btn--amber { background: var(--d-amber-btn); border-color: var(--d-amber-btn); color: #fff !important; }
.cd-btn--muted { color: var(--d-ink-2); }
.cd-btn:disabled { opacity: .5; cursor: not-allowed; }
.cd-done { display: flex; gap: 8px; align-items: center; padding: 14px 16px; color: var(--d-green-dark); font-weight: 600; }
.cd-bignote { padding: 10px 16px 0; color: var(--d-amber); font-size: 13px; }
.cd-biglist { padding: 4px 16px 10px; max-height: 420px; overflow: auto; }
.cd-bigrow { display: grid; grid-template-columns: 26px minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.4fr) 140px; gap: 10px; align-items: center; padding: 7px 0; border-top: 1px solid var(--d-line-soft); font-size: 13px; cursor: pointer; }
.cd-bigrow:first-child { border-top: 0; }
@media (max-width: 760px) {
  .cd-grid { grid-template-columns: minmax(0, 1fr) !important; }
  .cd-bigrow { grid-template-columns: 26px minmax(0, 1fr); }
  .cd-bigrow > :nth-child(n+3) { grid-column: 2; }
}
</style>
